'use client';

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Accessibility,
  X,
  Eye,
  Volume2,
  Brain,
  Hand,
  Settings as SettingsIcon,
  RotateCcw,
  HelpCircle,
  Type,
  Minus,
  Plus,
  Contrast,
  AlignJustify,
  CheckCircle2,
  Search,
  Link2,
  Keyboard,
  ChevronDown,
  Save,
  Headphones,
  ImageIcon,
  BookOpen,
  Sparkles,
  Square,
  Mic,
} from 'lucide-react';
import {
  isMacPlatform,
  readingMaskModifierLabel,
} from '@/lib/a11y-keyboard-shortcuts';
import {
  LYRRA_A11Y_STORAGE_KEY,
  LYRRA_A11Y_UPDATED_EVENT,
} from '@/hooks/useKeyboardNavigationSetting';
import { getAppName, isLyrraOne } from '@/lib/platform';
import { LyrraOneLogo } from '@/components/brand/LyrraOneLogo';
import {
  type A11ySettings,
  type A11yPreset,
  A11Y_PRESET_SETTINGS,
  applyA11ySettingsToDocument,
  DEFAULT_A11Y_SETTINGS,
  readA11ySettingsFromStorage,
  isA11yPresetActiveOnSettings,
  mergeA11yPresetIntoSettings,
  removeA11yPresetFromSettings,
} from '@/lib/lyrra-a11y-settings';
import { Switch } from '@/components/ui/Switch';
import A11yVoiceGuide from '@/components/accessibility/A11yVoiceGuide';
import { applyGlobalSimplifiedTextToPage } from '@/lib/global-simplified-text';
import { toast } from '@/components/ui';
import { useIsMobile } from '@/hooks/useMediaQuery';

export type { A11ySettings };

type Preset = A11yPreset;
type Category = 'vision' | 'audio' | 'cognitive' | 'interaction' | 'advanced' | 'help';

const DEFAULT = DEFAULT_A11Y_SETTINGS;
const PRESET_SETTINGS = A11Y_PRESET_SETTINGS;

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useA11ySettings() {
  const [settings, setSettings] = useState<A11ySettings>(readA11ySettingsFromStorage);

  const update = useCallback((patch: Partial<A11ySettings>) => {
    setSettings((prev) => {
      const next = { ...prev, ...patch };
      localStorage.setItem(LYRRA_A11Y_STORAGE_KEY, JSON.stringify(next));
      applyA11ySettingsToDocument(next);
      queueMicrotask(() => window.dispatchEvent(new Event(LYRRA_A11Y_UPDATED_EVENT)));
      return next;
    });
  }, []);

  const applyPreset = useCallback((preset: Preset) => {
    const next = { ...PRESET_SETTINGS[preset] };
    localStorage.setItem(LYRRA_A11Y_STORAGE_KEY, JSON.stringify(next));
    applyA11ySettingsToDocument(next);
    setSettings(next);
    queueMicrotask(() => window.dispatchEvent(new Event(LYRRA_A11Y_UPDATED_EVENT)));
  }, []);

  const reset = useCallback(() => {
    localStorage.removeItem(LYRRA_A11Y_STORAGE_KEY);
    applyA11ySettingsToDocument(DEFAULT);
    setSettings(DEFAULT);
    queueMicrotask(() => window.dispatchEvent(new Event(LYRRA_A11Y_UPDATED_EVENT)));
  }, []);

  useEffect(() => {
    applyA11ySettingsToDocument(settings);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const sync = () => setSettings(readA11ySettingsFromStorage());
    window.addEventListener(LYRRA_A11Y_UPDATED_EVENT, sync);
    return () => window.removeEventListener(LYRRA_A11Y_UPDATED_EVENT, sync);
  }, []);

  return { settings, update, applyPreset, reset };
}

// ── Helpers : niveaux discrets pour les tuiles ────────────────────────────────

type SpacingLevel = 'normal' | 'comfortable' | 'large';

const SPACING_PRESETS: Record<SpacingLevel, Pick<A11ySettings, 'lineHeight' | 'letterSpacing' | 'wordSpacing'>> = {
  normal: { lineHeight: 1.55, letterSpacing: 0, wordSpacing: 0 },
  comfortable: { lineHeight: 1.75, letterSpacing: 0.03, wordSpacing: 0.06 },
  large: { lineHeight: 1.9, letterSpacing: 0.05, wordSpacing: 0.12 },
};

function detectSpacingLevel(s: A11ySettings): SpacingLevel {
  if (s.lineHeight >= 1.85 || s.letterSpacing >= 0.04 || s.wordSpacing >= 0.1) return 'large';
  if (s.lineHeight >= 1.7 || s.letterSpacing >= 0.025 || s.wordSpacing >= 0.05) return 'comfortable';
  return 'normal';
}

// ── Atoms ─────────────────────────────────────────────────────────────────────

function Toggle({
  id,
  checked,
  onChange,
  disabled = false,
}: {
  id: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return <Switch id={id} checked={checked} onChange={onChange} size="md" disabled={disabled} className="shrink-0" />;
}

function ToggleRow({
  id,
  icon: Icon,
  label,
  desc,
  checked,
  onChange,
  disabled = false,
}: {
  id: string;
  icon: React.ComponentType<{ className?: string; size?: number }>;
  label: string;
  desc: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label
      htmlFor={id}
      className={cn(
        'group flex cursor-pointer items-center gap-4 rounded-2xl border bg-white px-4 py-3.5 transition-all',
        'border-gray-200 hover:border-[color:var(--inst-player-primary,#0C1C38)]/40 hover:shadow-[0_8px_22px_rgba(12,28,56,0.06)]',
        'dark:bg-zinc-900 dark:border-zinc-700 dark:hover:border-[color:var(--inst-player-primary,#0C1C38)]/50',
        checked && 'border-[color:var(--inst-player-primary,#0C1C38)]/35 bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_4%,white)] dark:bg-zinc-900',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-colors',
          checked
            ? 'bg-[color:var(--inst-player-primary,#0C1C38)] text-white'
            : 'bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_8%,white)] text-[color:var(--inst-player-primary,#0C1C38)] dark:bg-zinc-800 dark:text-zinc-200',
        )}
      >
        <Icon className="h-5 w-5" />
      </span>
      <div className="min-w-0 flex-1 space-y-0.5">
        <p className="text-[15px] font-bold uppercase tracking-wide text-gray-900 dark:text-zinc-50">{label}</p>
        <p className="text-[13px] leading-snug text-gray-600 dark:text-zinc-400">{desc}</p>
      </div>
      <Toggle id={id} checked={checked} onChange={onChange} disabled={disabled} />
    </label>
  );
}

function CategoryButton({
  active,
  icon: Icon,
  label,
  sublabel,
  onClick,
  variant = 'category',
}: {
  active?: boolean;
  icon: React.ComponentType<{ className?: string; size?: number }>;
  label: string;
  sublabel?: string;
  onClick: () => void;
  variant?: 'category' | 'utility';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      role="tab"
      aria-selected={!!active}
      className={cn(
        'flex w-full items-center gap-3 rounded-2xl px-3.5 py-3 text-left transition-all',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--inst-player-primary,#0C1C38)]',
        active
          ? 'bg-white text-[color:var(--inst-player-primary,#0C1C38)] shadow-[0_10px_28px_rgba(0,0,0,0.18)]'
          : variant === 'utility'
            ? 'bg-white/5 text-white/85 hover:bg-white/10'
            : 'text-white/85 hover:bg-white/10',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
          active
            ? 'bg-[color:var(--inst-player-accent,var(--inst-player-primary,#0C1C38))] text-white'
            : 'bg-white/10 text-white',
        )}
      >
        <Icon className="h-[18px] w-[18px]" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-bold uppercase tracking-wider leading-tight">
          {label}
        </span>
        {sublabel && (
          <span
            className={cn(
              'mt-0.5 block text-[11px] leading-tight',
              active ? 'text-gray-500' : 'text-white/55',
            )}
          >
            {sublabel}
          </span>
        )}
      </span>
    </button>
  );
}

function SectionTile({
  icon: Icon,
  label,
  active = false,
  children,
}: {
  icon: React.ComponentType<{ className?: string; size?: number }>;
  label: string;
  active?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-between gap-3 rounded-[20px] border bg-white px-3 py-4 text-center transition-colors dark:bg-zinc-900',
        active
          ? 'border-[color:var(--inst-player-primary,#0C1C38)] shadow-[0_0_0_3px_color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_10%,transparent)]'
          : 'border-gray-200 hover:border-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_30%,transparent)] dark:border-zinc-700',
      )}
    >
      <span
        aria-hidden="true"
        className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_8%,white)] text-[color:var(--inst-player-primary,#0C1C38)] dark:bg-zinc-800 dark:text-zinc-100"
      >
        <Icon className="h-6 w-6" />
      </span>
      <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-gray-700 dark:text-zinc-300">
        {label}
      </p>
      <div className="w-full">{children}</div>
    </div>
  );
}

function PillSelect<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
}: {
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (v: T) => void;
  ariaLabel: string;
}) {
  const id = useMemo(() => `pill-sel-${Math.random().toString(36).slice(2)}`, []);
  return (
    <div className="relative w-full">
      <label htmlFor={id} className="sr-only">
        {ariaLabel}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className={cn(
          'w-full appearance-none rounded-xl bg-[color:var(--inst-player-primary,#0C1C38)] py-2.5 pl-3 pr-9 text-[13px] font-bold uppercase tracking-wider text-white',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--inst-player-primary,#0C1C38)]',
        )}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown
        aria-hidden="true"
        className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white"
      />
    </div>
  );
}

function SliderRow({
  label,
  id,
  min,
  max,
  step,
  value,
  display,
  onChange,
}: {
  label: string;
  id: string;
  min: number;
  max: number;
  step: number;
  value: number;
  display: string;
  onChange: (v: number) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <label htmlFor={id} className="text-sm font-semibold text-gray-700 dark:text-zinc-300">
          {label}
        </label>
        <span className="rounded-full bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_10%,transparent)] px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-[color:var(--inst-player-primary,#0C1C38)]">
          {display}
        </span>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        aria-label={label}
        className="a11y-range w-full accent-[color:var(--inst-player-primary,#0C1C38)]"
      />
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

interface A11yToolbarProps {
  /** When provided, the component is controlled — no standalone FAB is rendered */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Position du bouton flottant quand non contrôlé */
  fabPosition?: 'left' | 'right';
  /** Position du panneau (rétro-compatibilité ; le nouveau panneau est centré) */
  panelPosition?: 'left' | 'right';
  /** Player EduFlow : cible DOM du panneau pour la loupe de mise au point */
  magnifierPanelRef?: React.Ref<HTMLElement>;
  /** Validation externe avant activation du mode texte simplifié (ex. bloquer en preview). */
  onBeforeToggleSimplifiedTextMode?: (nextEnabled: boolean) => Promise<boolean> | boolean;
}

const A11yToolbar: React.FC<A11yToolbarProps> = ({
  open: openProp,
  onOpenChange,
  fabPosition = 'right',
  magnifierPanelRef,
  onBeforeToggleSimplifiedTextMode,
}) => {
  const { t, i18n } = useTranslation();
  const [openInternal, setOpenInternal] = useState(false);
  const [category, setCategory] = useState<Category>('vision');
  const [voiceGuideOpen, setVoiceGuideOpen] = useState(false);
  const localeKey = i18n.resolvedLanguage ?? i18n.language ?? 'fr';
  const isMobile = useIsMobile();
  const maskModifier = readingMaskModifierLabel();
  const isMac = isMacPlatform();

  const controlled = openProp !== undefined;
  const open = controlled ? openProp : openInternal;
  const setOpen = (v: boolean) => {
    if (controlled) onOpenChange?.(v);
    else setOpenInternal(v);
  };

  const openAccessibilityCenter = useCallback(() => {
    if (isMobile) setVoiceGuideOpen(true);
    else setOpen(true);
  }, [isMobile, setOpen]);

  useEffect(() => {
    if (!open || !isMobile) return;
    setVoiceGuideOpen(true);
    setOpen(false);
  }, [open, isMobile, setOpen]);

  const showDesktopPanel = open && !isMobile;

  const { settings, update, reset } = useA11ySettings();
  const [simplifiedLoading, setSimplifiedLoading] = useState(false);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const panelRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!showDesktopPanel) return;
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      previouslyFocusedRef.current?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDesktopPanel]);

  const spacingLevel = detectSpacingLevel(settings);
  const setSpacingLevel = (level: SpacingLevel) => update(SPACING_PRESETS[level]);

  const fontPercent = Math.round(settings.fontScale * 100);
  const adjustFont = (delta: number) => {
    const next = Math.min(135, Math.max(90, fontPercent + delta));
    update({ fontScale: next / 100 });
  };

  const categoryMeta: Record<Exclude<Category, 'help'>, { icon: typeof Eye; title: string; subtitle: string }> = {
    vision: {
      icon: Eye,
      title: t('a11y.cat.vision.title', 'Voir'),
      subtitle: t('a11y.cat.vision.subtitle', "Adaptez l'affichage à vos besoins visuels"),
    },
    audio: {
      icon: Volume2,
      title: t('a11y.cat.audio.title', 'Entendre'),
      subtitle: t('a11y.cat.audio.subtitle', "Audio et description sonore à la demande"),
    },
    cognitive: {
      icon: Brain,
      title: t('a11y.cat.cognitive.title', 'Comprendre'),
      subtitle: t('a11y.cat.cognitive.subtitle', 'Simplifiez la compréhension et la charge cognitive'),
    },
    interaction: {
      icon: Hand,
      title: t('a11y.cat.interaction.title', 'Interagir'),
      subtitle: t('a11y.cat.interaction.subtitle', 'Navigation au clavier et interactions'),
    },
    advanced: {
      icon: SettingsIcon,
      title: t('a11y.cat.advanced.title', 'Réglages avancés'),
      subtitle: t('a11y.cat.advanced.subtitle', 'Profils et ajustements fins'),
    },
  };

  const headerMeta =
    category === 'help'
      ? {
          icon: HelpCircle,
          title: t('a11y.cat.help.title', "Besoin d'aide"),
          subtitle: t('a11y.cat.help.subtitle', 'Raccourcis et conseils accessibilité'),
        }
      : categoryMeta[category];
  const HeaderIcon = headerMeta.icon;

  return (
    <>
      {/* Assistant vocal accessibilité (modal sur demande) */}
      <A11yVoiceGuide open={voiceGuideOpen} onClose={() => setVoiceGuideOpen(false)} />

      {/* FAB trigger — only in standalone (uncontrolled) mode */}
      {!controlled && (
        <button
          type="button"
          onClick={openAccessibilityCenter}
          aria-label={
            isMobile
              ? t(
                  'a11y.voiceGuide.cta.openLabel',
                  "Ouvrir l'assistant vocal — décrire mes besoins d'accessibilité à voix haute",
                )
              : t('accessibility.openCenter')
          }
          className={cn(
            'fixed z-[10050]',
            fabPosition === 'left' ? 'start-3 sm:start-6' : 'end-3 sm:end-6',
            'bottom-20 sm:bottom-6',
            'flex h-12 w-12 items-center justify-center rounded-full shadow-lg',
            'bg-[color:var(--inst-player-primary,#0C1C38)] text-white transition-colors hover:bg-[color:var(--inst-player-primary-hover,#19335E)]',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--inst-player-primary,#0C1C38)] focus-visible:ring-offset-2',
          )}
          data-a11y-fab="true"
        >
          <Accessibility size={22} />
        </button>
      )}

      {/* Backdrop */}
      <AnimatePresence>
        {showDesktopPanel && (
          <motion.div
            key="a11y-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 z-[10048] bg-black/45 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Panel */}
      <AnimatePresence>
        {showDesktopPanel && (
          <motion.section
            ref={(node) => {
              panelRef.current = node;
              if (typeof magnifierPanelRef === 'function') magnifierPanelRef(node);
              else if (magnifierPanelRef) (magnifierPanelRef as React.MutableRefObject<HTMLElement | null>).current = node;
            }}
            key={`a11y-panel-${localeKey}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="a11y-panel-title"
            aria-describedby="a11y-panel-subtitle"
            data-a11y-panel="true"
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
            className={cn(
              'fixed inset-0 z-[10049] m-auto flex flex-col overflow-hidden rounded-[28px] shadow-2xl',
              'h-[min(94vh,46rem)] w-[min(96vw,56rem)]',
              'bg-white dark:bg-zinc-900',
            )}
          >
            <div className="grid h-full min-h-0 grid-cols-[260px_minmax(0,1fr)]">
              {/* ──────── Sidebar ──────── */}
              <aside
                role="tablist"
                aria-orientation="vertical"
                aria-label={t('accessibility.center')}
                data-a11y-panel-sidebar="true"
                className="flex min-h-0 flex-col gap-3 overflow-y-auto overscroll-contain bg-[color:var(--inst-player-primary,#0C1C38)] p-5 text-white scrollbar-thin scrollbar-dark md:gap-4"
              >
                {isLyrraOne() ? (
                  <LyrraOneLogo variant="full" size="sm" linkTo={false} onDark className="max-w-[148px]" />
                ) : (
                  <div className="flex items-center gap-2.5">
                    <span
                      aria-hidden="true"
                      className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-[color:var(--inst-player-primary,#0C1C38)] shadow-[0_8px_18px_rgba(0,0,0,0.25)]"
                    >
                      <span className="text-lg font-black">L</span>
                    </span>
                    <span className="leading-tight">
                      <span className="block text-base font-extrabold">LYRRA</span>
                      <span className="block text-[11px] uppercase tracking-[0.22em] text-white/70">
                        Studio
                      </span>
                    </span>
                  </div>
                )}

                <div className="space-y-1 pt-1">
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/65">
                    {t('a11y.sidebar.section', 'Accessibilité')}
                  </p>
                  <p id="a11y-panel-subtitle" className="text-[12px] leading-snug text-white/70">
                    {t('a11y.sidebar.lead', "Personnalisez l'expérience selon vos besoins")}
                  </p>
                </div>

                {/* Gros bouton « assistant vocal » : décrivez vos besoins à voix haute */}
                <button
                  type="button"
                  onClick={() => setVoiceGuideOpen(true)}
                  aria-label={t(
                    'a11y.voiceGuide.cta.openLabel',
                    "Ouvrir l'assistant vocal — décrire mes besoins d'accessibilité à voix haute",
                  )}
                  className={cn(
                    'group relative flex w-full flex-col items-center gap-2 rounded-2xl border-2 border-dashed border-white/30 bg-white/[0.06] px-3 py-4 text-center transition-all hover:border-white/60 hover:bg-white/[0.12]',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--inst-player-primary,#0C1C38)]',
                  )}
                >
                  <span
                    aria-hidden="true"
                    className="flex h-16 w-16 items-center justify-center rounded-full bg-white text-[color:var(--inst-player-accent,var(--inst-player-primary,#0C1C38))] shadow-[0_12px_28px_rgba(0,0,0,0.35)] transition-transform group-hover:scale-105"
                  >
                    <Mic className="h-8 w-8" />
                  </span>
                  <span className="block">
                    <span className="block text-[12px] font-extrabold uppercase tracking-wider text-white">
                      {t('a11y.voiceGuide.cta.title', 'Décrivez vos besoins')}
                    </span>
                    <span className="mt-1 block text-[11px] leading-snug text-white/75">
                      {t(
                        'a11y.voiceGuide.cta.hint',
                        "Parlez : l'IA active automatiquement les bonnes options pour vous.",
                      )}
                    </span>
                  </span>
                </button>

                <nav className="mt-1 flex flex-col gap-1.5">
                  {(['vision', 'audio', 'cognitive', 'interaction', 'advanced'] as const).map((c) => {
                    const m = categoryMeta[c];
                    return (
                      <CategoryButton
                        key={c}
                        active={category === c}
                        icon={m.icon}
                        label={m.title}
                        sublabel={m.subtitle}
                        onClick={() => setCategory(c)}
                      />
                    );
                  })}
                </nav>

                <div className="mt-auto space-y-2 border-t border-white/15 pt-3">
                  <CategoryButton
                    icon={RotateCcw}
                    label={t('a11y.action.resetAll', 'Réinitialiser tout')}
                    sublabel={t('a11y.action.resetAllHint', 'Revenir aux réglages par défaut')}
                    onClick={reset}
                    variant="utility"
                  />
                  <CategoryButton
                    icon={HelpCircle}
                    label={t('a11y.action.help', "Besoin d'aide ?")}
                    sublabel={t('a11y.action.helpHint', "Guide d'utilisation et ressources")}
                    onClick={() => setCategory('help')}
                    variant="utility"
                    active={category === 'help'}
                  />
                </div>
              </aside>

              {/* ──────── Main panel ──────── */}
              <div className="flex h-full min-h-0 flex-col bg-white dark:bg-zinc-900">
                {/* Header */}
                <header className="flex items-start justify-between gap-3 border-b border-gray-200 px-4 py-3 dark:border-zinc-700 sm:gap-4 sm:px-6 sm:py-4">
                  <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
                    <span
                      aria-hidden="true"
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_10%,white)] text-[color:var(--inst-player-primary,#0C1C38)] dark:bg-zinc-800 dark:text-zinc-100 sm:h-12 sm:w-12"
                    >
                      <HeaderIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                    </span>
                    <div className="min-w-0">
                      <h2
                        id="a11y-panel-title"
                        className="text-lg font-black uppercase tracking-tight text-gray-900 dark:text-white sm:text-2xl"
                      >
                        {headerMeta.title}
                      </h2>
                      <p className="text-xs text-gray-500 dark:text-zinc-400 sm:text-sm">
                        {headerMeta.subtitle}
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    aria-label={t('accessibility.closeCenter')}
                    className="flex shrink-0 items-center gap-2 rounded-2xl border border-gray-200 px-2.5 py-2 text-[11px] font-bold uppercase tracking-wider text-gray-700 transition-colors hover:bg-gray-50 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800 sm:px-3 sm:text-[12px]"
                  >
                    <X className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('btn.close', 'Fermer')}</span>
                  </button>
                </header>

                {/* Body */}
                <div className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain px-4 py-4 scrollbar-thin sm:space-y-6 sm:px-6 sm:py-5">
                  {category === 'vision' && (
                    <>
                      <div className="grid grid-cols-1 gap-3 min-[400px]:grid-cols-2 md:grid-cols-4">
                        <SectionTile icon={Type} label={t('a11y.tile.size', 'Taille du texte')}>
                          <div className="flex items-center justify-between gap-2 rounded-xl bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_8%,white)] px-2 py-1.5 dark:bg-zinc-800">
                            <button
                              type="button"
                              onClick={() => adjustFont(-5)}
                              aria-label={t('a11y.tile.sizeDecrease', 'Diminuer la taille')}
                              className="flex h-7 w-7 items-center justify-center rounded-lg bg-white text-[color:var(--inst-player-primary,#0C1C38)] shadow-sm hover:bg-gray-50 dark:bg-zinc-700 dark:text-zinc-50"
                            >
                              <Minus className="h-4 w-4" />
                            </button>
                            <span className="text-sm font-extrabold text-[color:var(--inst-player-primary,#0C1C38)] dark:text-zinc-50">
                              {fontPercent}%
                            </span>
                            <button
                              type="button"
                              onClick={() => adjustFont(5)}
                              aria-label={t('a11y.tile.sizeIncrease', 'Augmenter la taille')}
                              className="flex h-7 w-7 items-center justify-center rounded-lg bg-white text-[color:var(--inst-player-primary,#0C1C38)] shadow-sm hover:bg-gray-50 dark:bg-zinc-700 dark:text-zinc-50"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          </div>
                        </SectionTile>

                        <SectionTile icon={Contrast} label={t('a11y.tile.contrast', 'Contraste')}>
                          <PillSelect
                            ariaLabel={t('a11y.tile.contrast', 'Contraste')}
                            value={settings.highContrast ? 'high' : 'normal'}
                            onChange={(v) => update({ highContrast: v === 'high' })}
                            options={[
                              { value: 'normal', label: t('a11y.contrast.normal', 'Normal') },
                              { value: 'high', label: t('a11y.contrast.high', 'Élevé') },
                            ]}
                          />
                        </SectionTile>

                        <SectionTile icon={AlignJustify} label={t('a11y.tile.spacing', 'Espacement')}>
                          <PillSelect
                            ariaLabel={t('a11y.tile.spacing', 'Espacement')}
                            value={spacingLevel}
                            onChange={(v) => setSpacingLevel(v)}
                            options={[
                              { value: 'normal', label: t('a11y.spacing.normal', 'Normal') },
                              { value: 'comfortable', label: t('a11y.spacing.comfortable', 'Confortable') },
                              { value: 'large', label: t('a11y.spacing.large', 'Large') },
                            ]}
                          />
                        </SectionTile>

                        <SectionTile icon={BookOpen} label={t('a11y.tile.font', 'Police')}>
                          <PillSelect
                            ariaLabel={t('a11y.tile.font', 'Police')}
                            value={settings.dyslexiaFont ? 'dys' : 'standard'}
                            onChange={(v) => update({ dyslexiaFont: v === 'dys' })}
                            options={[
                              { value: 'standard', label: t('a11y.font.standard', 'Standard') },
                              { value: 'dys', label: t('a11y.font.dys', 'Lisible') },
                            ]}
                          />
                        </SectionTile>
                      </div>

                      <div className="space-y-2.5">
                        <ToggleRow
                          id="a11y-underline-links"
                          icon={Link2}
                          label={t('accessibility.underlineLinks')}
                          desc={t('accessibility.underlineLinksDesc')}
                          checked={settings.underlineLinks}
                          onChange={(v) => update({ underlineLinks: v })}
                        />
                        <ToggleRow
                          id="a11y-mouse-magnifier"
                          icon={Search}
                          label={t('accessibility.mouseMagnifier', 'Loupe de lecture')}
                          desc={t(
                            'accessibility.mouseMagnifierDesc',
                            'Affiche une loupe qui suit le curseur et grossit le contenu sur la zone pointée.',
                          )}
                          checked={settings.mouseMagnifier}
                          onChange={(v) => update({ mouseMagnifier: v })}
                        />
                        <ToggleRow
                          id="a11y-reading-mask"
                          icon={Square}
                          label={t('accessibility.readingMask', 'Masque de lecture')}
                          desc={t(
                            'accessibility.readingMaskDesc',
                            'Une bande opaque suit le curseur pour isoler la ligne en cours — très utile pour la dyslexie et les TDA/H.',
                          )}
                          checked={settings.readingMask}
                          onChange={(v) => update({ readingMask: v })}
                        />
                      </div>

                      {/* Aperçu */}
                      <div className="rounded-2xl border border-gray-200 bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_3%,white)] px-4 py-4 dark:border-zinc-700 dark:bg-zinc-900/40">
                        <div className="mb-3 flex items-center justify-between">
                          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gray-700 dark:text-zinc-300">
                            {t('a11y.preview.label', 'Aperçu')}
                          </p>
                          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 dark:text-zinc-400">
                            <Eye className="h-3.5 w-3.5" />
                            {t('a11y.preview.live', 'En direct')}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 items-center gap-3 md:grid-cols-[1fr_auto]">
                          <div className="space-y-2">
                            <p
                              className="text-3xl font-black text-[color:var(--inst-player-primary,#0C1C38)] dark:text-zinc-50"
                              style={{
                                fontFamily: settings.dyslexiaFont ? 'OpenDyslexic, Arial, sans-serif' : undefined,
                                letterSpacing: `${settings.letterSpacing}em`,
                              }}
                            >
                              Aa
                            </p>
                            <p
                              className="text-sm text-gray-700 dark:text-zinc-200"
                              style={{
                                lineHeight: settings.lineHeight,
                                letterSpacing: `${settings.letterSpacing}em`,
                                wordSpacing: `${settings.wordSpacing}em`,
                                fontFamily: settings.dyslexiaFont ? 'OpenDyslexic, Arial, sans-serif' : undefined,
                              }}
                            >
                              {t('a11y.preview.text', '{{appName}} adapte l\'affichage à vos besoins. Cet aperçu reflète vos réglages en direct.', {
                                appName: getAppName(),
                              })}
                            </p>
                          </div>
                          <div
                            className="hidden h-16 w-16 items-center justify-center rounded-full border-2 border-[color:var(--inst-player-primary,#0C1C38)]/70 bg-white text-[color:var(--inst-player-primary,#0C1C38)] shadow-sm md:flex"
                            aria-hidden="true"
                          >
                            <Search className="h-7 w-7" />
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {category === 'audio' && (
                    <div className="space-y-2.5">
                      <ToggleRow
                        id="a11y-speak-on-selection"
                        icon={Headphones}
                        label={t('accessibility.speakOnSelection', 'Synthèse vocale sur sélection')}
                        desc={t(
                          'accessibility.speakOnSelectionDesc',
                          "Sélectionnez du texte : un bouton apparaît pour le lire à voix haute à la demande (différent du mode lecture audio automatique).",
                        )}
                        checked={settings.speakOnSelection}
                        onChange={(v) => update({ speakOnSelection: v })}
                      />
                      <ToggleRow
                        id="a11y-image-hover-audio"
                        icon={ImageIcon}
                        label={t('accessibility.imageHoverAudio', 'Description audio image au survol')}
                        desc={t(
                          'accessibility.imageHoverAudioDesc',
                          "Quand une image est survolée dans le player, la description audio démarre automatiquement.",
                        )}
                        checked={settings.autoPlayImageDescriptionOnHover}
                        onChange={(v) => update({ autoPlayImageDescriptionOnHover: v })}
                      />
                    </div>
                  )}

                  {category === 'cognitive' && (
                    <div className="space-y-2.5">
                      <ToggleRow
                        id="a11y-simplified-text-mode"
                        icon={Sparkles}
                        label={t('accessibility.simplifiedTextMode', 'Mode texte simplifié')}
                        desc={t(
                          'accessibility.simplifiedTextModeDesc',
                          'Simplifie les textes de la page affichée par IA (mise en page conservée). Dans une formation, les textes du parcours sont traités bloc par bloc.',
                        )}
                        checked={settings.simplifiedTextMode}
                        disabled={simplifiedLoading}
                        onChange={(v) => {
                          void (async () => {
                            if (simplifiedLoading) return;
                            setSimplifiedLoading(true);
                            try {
                              const ok = onBeforeToggleSimplifiedTextMode
                                ? await onBeforeToggleSimplifiedTextMode(v)
                                : await applyGlobalSimplifiedTextToPage(v);
                              if (!ok) return;
                              update({ simplifiedTextMode: v });
                            } catch {
                              toast.error(
                                t(
                                  'accessibility.simplifiedTextPageError',
                                  "Impossible de simplifier le contenu de cette page pour le moment. Réessayez dans un instant.",
                                ),
                              );
                            } finally {
                              setSimplifiedLoading(false);
                            }
                          })();
                        }}
                      />
                      <ToggleRow
                        id="a11y-reduced-motion"
                        icon={CheckCircle2}
                        label={t('accessibility.reducedMotion')}
                        desc={t('accessibility.reducedMotionDesc')}
                        checked={settings.reducedMotion}
                        onChange={(v) => update({ reducedMotion: v })}
                      />
                    </div>
                  )}

                  {category === 'interaction' && (
                    <div className="space-y-2.5">
                      <ToggleRow
                        id="a11y-keyboard-nav"
                        icon={Keyboard}
                        label={t('accessibility.keyboardNavigation')}
                        desc={t('accessibility.keyboardNavigationDesc')}
                        checked={settings.keyboardNavigation}
                        onChange={(v) => update({ keyboardNavigation: v })}
                      />
                    </div>
                  )}

                  {category === 'advanced' && (
                    <div className="space-y-5">
                      <div className="space-y-2.5">
                        <div className="flex items-baseline justify-between gap-2">
                          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gray-700 dark:text-zinc-300">
                            {t('accessibility.presets', 'Profils')}
                          </p>
                          <p className="text-[11px] text-gray-500 dark:text-zinc-400">
                            {t('accessibility.preset.cumulHint', 'Combinez plusieurs profils si besoin')}
                          </p>
                        </div>
                        <div className="grid gap-2 md:grid-cols-3">
                          {(['standard', 'malvoyance', 'dys'] as Preset[]).map((key) => {
                            const meta: Record<Preset, { label: string; desc: string }> = {
                              standard: {
                                label: t('accessibility.preset.standard.label'),
                                desc: t('accessibility.preset.standard.desc'),
                              },
                              malvoyance: {
                                label: t('accessibility.preset.lowVision.label'),
                                desc: t('accessibility.preset.lowVision.desc'),
                              },
                              dys: {
                                label: t('accessibility.preset.dys.label'),
                                desc: t('accessibility.preset.dys.desc'),
                              },
                            };
                            const p = PRESET_SETTINGS[key];
                            const isStandard = key === 'standard';
                            const active = isStandard
                              ? !isA11yPresetActiveOnSettings(settings, PRESET_SETTINGS.malvoyance) &&
                                !isA11yPresetActiveOnSettings(settings, PRESET_SETTINGS.dys) &&
                                JSON.stringify(settings) === JSON.stringify(DEFAULT_A11Y_SETTINGS)
                              : isA11yPresetActiveOnSettings(settings, p);

                            const handleClick = () => {
                              if (isStandard) {
                                // Reset complet
                                Object.entries(DEFAULT_A11Y_SETTINGS).forEach(([k, v]) =>
                                  update({ [k as keyof A11ySettings]: v } as Partial<A11ySettings>),
                                );
                                return;
                              }
                              const next = active
                                ? removeA11yPresetFromSettings(
                                    settings,
                                    p,
                                    (['malvoyance', 'dys'] as Preset[])
                                      .filter((other) => other !== key)
                                      .filter((other) =>
                                        isA11yPresetActiveOnSettings(settings, PRESET_SETTINGS[other]),
                                      )
                                      .map((other) => PRESET_SETTINGS[other]),
                                  )
                                : mergeA11yPresetIntoSettings(settings, p);
                              Object.entries(next).forEach(([k, v]) =>
                                update({ [k as keyof A11ySettings]: v } as Partial<A11ySettings>),
                              );
                            };

                            return (
                              <button
                                key={key}
                                type="button"
                                onClick={handleClick}
                                aria-pressed={!isStandard ? active : undefined}
                                className={cn(
                                  'relative rounded-2xl border px-4 py-3 text-left transition-all',
                                  active
                                    ? 'border-[color:var(--inst-player-primary,#0C1C38)] bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_8%,white)] shadow-[0_4px_14px_color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_18%,transparent)] dark:bg-zinc-800'
                                    : 'border-gray-200 hover:border-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_30%,transparent)] dark:border-zinc-700',
                                )}
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    <p className="text-sm font-bold text-gray-900 dark:text-zinc-50">
                                      {meta[key].label}
                                    </p>
                                    <p className="mt-0.5 text-[12px] text-gray-500 dark:text-zinc-400">
                                      {meta[key].desc}
                                    </p>
                                  </div>
                                  {!isStandard && active && (
                                    <span
                                      aria-hidden="true"
                                      className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[color:var(--inst-player-primary,#0C1C38)] text-white"
                                    >
                                      <CheckCircle2 className="h-3.5 w-3.5" />
                                    </span>
                                  )}
                                </div>
                                {!isStandard && (
                                  <p className="mt-2 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[color:var(--inst-player-primary,#0C1C38)] dark:text-zinc-300">
                                    {active
                                      ? t('accessibility.preset.clickToRemove', 'Cliquer pour retirer')
                                      : t('accessibility.preset.clickToAdd', 'Cliquer pour ajouter')}
                                  </p>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div className="space-y-4 rounded-2xl border border-gray-200 px-4 py-4 dark:border-zinc-700">
                        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gray-700 dark:text-zinc-300">
                          {t('accessibility.typography', 'Typographie')}
                        </p>
                        <SliderRow
                          id="a11y-font-scale"
                          label={t('accessibility.fontScale')}
                          min={0.9}
                          max={1.35}
                          step={0.01}
                          value={settings.fontScale}
                          display={settings.fontScale.toFixed(2) + 'x'}
                          onChange={(v) => update({ fontScale: v })}
                        />
                        <SliderRow
                          id="a11y-line-height"
                          label={t('accessibility.lineHeight')}
                          min={1.35}
                          max={2.2}
                          step={0.05}
                          value={settings.lineHeight}
                          display={settings.lineHeight.toFixed(2)}
                          onChange={(v) => update({ lineHeight: v })}
                        />
                        <SliderRow
                          id="a11y-letter-spacing"
                          label={t('accessibility.letterSpacing')}
                          min={0}
                          max={0.08}
                          step={0.005}
                          value={settings.letterSpacing}
                          display={settings.letterSpacing.toFixed(3) + 'em'}
                          onChange={(v) => update({ letterSpacing: v })}
                        />
                        <SliderRow
                          id="a11y-word-spacing"
                          label={t('accessibility.wordSpacing')}
                          min={0}
                          max={0.18}
                          step={0.01}
                          value={settings.wordSpacing}
                          display={settings.wordSpacing.toFixed(2) + 'em'}
                          onChange={(v) => update({ wordSpacing: v })}
                        />
                      </div>

                      <div className="rounded-2xl border border-gray-200 px-4 py-4 text-sm dark:border-zinc-700">
                        <p className="font-bold text-gray-900 dark:text-zinc-50">
                          {t('accessibility.alwaysOn')}
                        </p>
                        <ul className="mt-2 space-y-1 text-[13px] text-gray-500 dark:text-zinc-400">
                          <li>{t('accessibility.alwaysOnFocus')}</li>
                          <li>{t('accessibility.alwaysOnLinks')}</li>
                          <li>{t('accessibility.alwaysOnSaved')}</li>
                        </ul>
                      </div>
                    </div>
                  )}

                  {category === 'help' && (
                    <div className="space-y-4 text-sm leading-relaxed text-gray-700 dark:text-zinc-200">
                      <div className="rounded-2xl border border-gray-200 px-4 py-4 dark:border-zinc-700">
                        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gray-700 dark:text-zinc-300">
                          {t('a11y.help.shortcuts', 'Raccourcis clavier')}
                        </p>
                        <ul className="mt-2 space-y-2">
                          <li>
                            <kbd className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs dark:bg-zinc-800">Échap</kbd>{' '}
                            <span className="text-gray-500 dark:text-zinc-400">(Esc)</span>{' '}
                            — {t('a11y.help.kbdEsc', 'Fermer le panneau / arrêter la synthèse vocale')}
                          </li>
                          <li>
                            <span className="inline-flex flex-wrap items-center gap-1">
                              {isMac ? (
                                <>
                                  <kbd className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs dark:bg-zinc-800">
                                    {maskModifier} + =/+
                                  </kbd>
                                  <span className="text-gray-400">·</span>
                                  <kbd className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs dark:bg-zinc-800">
                                    {maskModifier} + −
                                  </kbd>
                                  <span className="text-gray-400">·</span>
                                  <kbd className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs dark:bg-zinc-800">
                                    {maskModifier} + ↑/↓
                                  </kbd>
                                </>
                              ) : (
                                <>
                                  <kbd className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs dark:bg-zinc-800">
                                    {maskModifier} + +
                                  </kbd>
                                  <span className="text-gray-400">·</span>
                                  <kbd className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs dark:bg-zinc-800">
                                    {maskModifier} + −
                                  </kbd>
                                </>
                              )}
                            </span>
                            <span className="mt-0.5 block text-[13px]">
                              {t('a11y.help.kbdMask', 'Ajuster la hauteur du masque de lecture (masque activé)')}
                            </span>
                            <span className="mt-0.5 block text-[11px] text-gray-500 dark:text-zinc-400">
                              {isMac
                                ? t(
                                    'a11y.help.kbdMaskMac',
                                    'Touche = ou + (au-dessus des chiffres), ou flèches ↑ ↓ avec Option ⌥',
                                  )
                                : t(
                                    'a11y.help.kbdMaskOther',
                                    'Windows / Linux : Alt + + / Alt + −',
                                  )}
                            </span>
                          </li>
                          <li>
                            <kbd className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs dark:bg-zinc-800">Tab</kbd>{' '}
                            — {t('a11y.help.kbdTab', 'Naviguer entre les éléments interactifs')}
                          </li>
                        </ul>
                      </div>
                      <div className="rounded-2xl border border-gray-200 px-4 py-4 dark:border-zinc-700">
                        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gray-700 dark:text-zinc-300">
                          {t('a11y.help.tips', 'Conseils')}
                        </p>
                        <ul className="mt-2 list-disc space-y-1.5 ps-5">
                          <li>{t('a11y.help.tip1', 'Vos réglages sont sauvegardés automatiquement sur cet appareil.')}</li>
                          <li>{t('a11y.help.tip2', 'Activez un profil dans « Réglages avancés » pour appliquer un ensemble cohérent.')}</li>
                          <li>{t('a11y.help.tip3', 'Le masque de lecture et la synthèse vocale sur sélection se cumulent avec le reste.')}</li>
                        </ul>
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <footer className="flex shrink-0 flex-col items-stretch gap-3 border-t border-gray-200 bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_3%,white)] px-4 py-3 dark:border-zinc-700 dark:bg-zinc-900/60 sm:px-6 sm:py-4 md:flex-row md:items-center md:justify-between">
                  <div className="flex items-start gap-2.5 text-[12px] text-gray-600 dark:text-zinc-300">
                    <span
                      aria-hidden="true"
                      className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[color:var(--inst-player-primary,#0C1C38)]/10 text-[color:var(--inst-player-primary,#0C1C38)]"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" />
                    </span>
                    <p className="leading-snug">
                      {t(
                        'a11y.footer.hint',
                        "Ces réglages s'adaptent automatiquement à vos préférences et sont sauvegardés sur cet appareil.",
                      )}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[color:var(--inst-player-primary,#0C1C38)] px-5 py-3 text-[13px] font-bold uppercase tracking-wider text-white shadow-[0_10px_24px_rgba(12,28,56,0.25)] transition-colors hover:bg-[color:var(--inst-player-primary-hover,#19335E)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--inst-player-primary,#0C1C38)]"
                  >
                    <Save className="h-4 w-4" />
                    {t('a11y.footer.save', 'Enregistrer mes préférences')}
                  </button>
                </footer>
              </div>
            </div>
          </motion.section>
        )}
      </AnimatePresence>
    </>
  );
};

export default A11yToolbar;
